import { Injectable, Injector, Compiler, NgModuleFactory, PLATFORM_ID, Inject } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { isPlatformBrowser } from '@angular/common';
import { ActivationConfig, RemoteConfig } from '../models/onboarding.types';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { Router } from '@angular/router';

@Injectable({
   providedIn: 'root'
})
export class OnboardingService {
   private apiUrl = environment.apiUrl;
   private modalRef?: NgbModalRef;
   private moduleFactoryPromise?: Promise<NgModuleFactory<any>>;
   private onboardingCompleted = false;
   private readonly excludedRoutes = ['/investing-coverage', '/supported-brokerages', '/supported-countries'];

   constructor(
      private modalService: NgbModal,
      private injector: Injector,
      private compiler: Compiler,
      private http: HttpClient,
      @Inject(PLATFORM_ID) private platformId: Object,
      private authService: AuthService,
      private apiService: ApiService,
      private subscriptionService: SubscriptionService,
      private router: Router
   ) {
      this.authService.isLoggedIn$.subscribe(isLoggedIn => {
         if (!isLoggedIn) {
            this.activationConfig = null;
            this.onboardingCompleted = false;
            if (isPlatformBrowser(this.platformId)) {
               sessionStorage.removeItem('musaffa_onboarding_shown_session');
            }
         }
      });
   }

   /**
    * Opens the onboarding modal.
    * Uses lazy loading for the entire OnboardingModule to ensure correct context (like FormsModule) is available.
    * SSR Safe: Will simply return null if called on the server.
    */
   public activationConfig: ActivationConfig | null = null;

   /**
    * Opens the onboarding modal using cached configuration.
    * Uses lazy loading for the entire OnboardingModule to ensure correct context (like FormsModule) is available.
    * SSR Safe: Will simply return null if called on the server.
    */
   async openOnboarding(): Promise<NgbModalRef | null> {
      // Prevent execution on server-side (SSR) as modals require DOM
      if (!isPlatformBrowser(this.platformId)) {
         return null;
      }

      // Check if current route is excluded
      if (this.isRouteExcluded()) {
         return null;
      }


      // For non_skippable, always show. For skippable/skippable_with_info, respect frequency.
      if (this.activationConfig && this.activationConfig.activation_screen_skippable !== 'non_skippable') {
         const remoteConfig = this.getRemoteConfigMock();
         if (!this.shouldShowOnboarding(remoteConfig)) {
            return null;
         }
      }

      if (this.modalRef) {
         return this.modalRef;
      }

      // Use cached activation config
      if (!this.activationConfig || !this.activationConfig.activation_screen_type) {
         return null;
      }

      // 1. Ensure the module is loaded and compiled once.
      if (!this.moduleFactoryPromise) {
         this.moduleFactoryPromise = import('../omboarding.module')
            .then(m => m.OnboardingModule ? this.compiler.compileModuleAsync(m.OnboardingModule) : Promise.reject('Module not found'));
      }

      const moduleFactory = await this.moduleFactoryPromise;
      
      // 2. Create a module instance for this modal session.
      const moduleRef = moduleFactory.create(this.injector);

      // 3. Load the modal component.
      const { OnboardingModalComponent } = await import(
         '../components/onboarding-modal/onboarding-modal.component'
      );

      const modalRef = this.modalService.open(OnboardingModalComponent, {
         windowClass: 'fullscreen-modal',
         backdrop: 'static',
         scrollable: true,
         animation: false,
         keyboard: false,
         injector: moduleRef.injector
      });
      
      if (modalRef.componentInstance) {
         modalRef.componentInstance.activationConfig = this.activationConfig;
      }

      this.modalRef = modalRef;
      if (isPlatformBrowser(this.platformId)) {
         sessionStorage.setItem('musaffa_onboarding_shown_session', 'true');
      }

      this.modalRef.result.finally(() => {
         this.modalRef = undefined;
         moduleRef.destroy(); // Clean up the module instance when modal closes.
         // For non_skippable, only record if onboarding was completed
         if (this.activationConfig?.activation_screen_skippable === 'non_skippable') {
            if (this.onboardingCompleted) {
               this.recordOnboardingShown();
            }
         } else {
            this.recordOnboardingShown();
         }
      });

      return this.modalRef;
   }

   getRemoteConfigMock(): RemoteConfig {
      return {
         activation_frequency: 'every_session',
         skip_warning_message: 'After March 21, phone verification will be mandatory.'
      };
   }

   private shouldShowOnboarding(config: RemoteConfig): boolean {
      if (!isPlatformBrowser(this.platformId)) return false;
      // If already shown this session (e.g. page refresh), don't show again
      if (sessionStorage.getItem('musaffa_onboarding_shown_session')) return false;

      if (config.activation_frequency === 'every_session') return true;
      
      if (!isPlatformBrowser(this.platformId)) return false;
      const lastShown = localStorage.getItem('musaffa_onboarding_last_shown');
      if (!lastShown) return true;

      const lastShownDate = new Date(lastShown);
      const today = new Date();

      if (config.activation_frequency === 'every_day') {
         return lastShownDate.toDateString() !== today.toDateString();
      }

      if (config.activation_frequency === 'every_x_days') {
         // Mock 3 days
         const diffTime = Math.abs(today.getTime() - lastShownDate.getTime());
         const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
         return diffDays >= 3;
      }

      return true;
   }



   private isRouteExcluded(): boolean {
      const currentUrl = this.router.url;
      // Check if current URL starts with or contains any of the excluded routes
      return this.excludedRoutes.some(route => currentUrl.includes(route));
   }

   private recordOnboardingShown() {
      if (isPlatformBrowser(this.platformId)) {
         localStorage.setItem('musaffa_onboarding_last_shown', new Date().toISOString());
      }
   }

   public markOnboardingCompleted() {
      this.onboardingCompleted = true;
   }

   public fetchActivationConfig(): Observable<ActivationConfig> {
      if (!this.authService.isLoggedIn()) {
         return of({
            activation_screen_type: null,
            phone_otp_required: false,
            activation_screen_skippable: 'skippable'
         } as ActivationConfig);
      }

      // MOCK DATA FOR TESTING
      // const mockConfig: ActivationConfig = {
      //    activation_screen_type: 'phone_verification_with_7_days_free',
      //    phone_otp_required: false,
      //    activation_screen_skippable: 'non_skippable'
      // };
      
      // this.activationConfig = mockConfig;
      // this.openOnboarding();
      // return of(mockConfig);

      // ORIGINAL API CALL
      return this.http.get<{status: boolean, data: ActivationConfig}>(`${this.apiUrl}/api/user/activation-state`).pipe(
         map(response => {
            let config: ActivationConfig;
            if (response && response.status && response.data) {
               config = response.data;
            } else {
               config = {
                  activation_screen_type: null,
                  phone_otp_required: false,
                  activation_screen_skippable: 'skippable'
               };
            }
      this.activationConfig = config;
            return config;
         }),
         // tap(config => {
         //    if (config && config.activation_screen_type) {
         //       this.openOnboarding();
         //    }
         // })
      );
   }

   async claimPremiumAccess() {
      // Only claim premium for screen types that offer a trial
      const screenType = this.activationConfig?.activation_screen_type;
      if (screenType === 'only_phone_verification' || !screenType) {
         return; // No premium to claim for this type
      }

      try {
         let response: any = await this.apiService.claimPremiumAccess();

         if (response.status_code === 'ALREADY_ACTIVATED' || response.status_code === 'ALREADY_CLAIMED') {
            this.authService.setOnboardingTrialClaimed(true);
            await this.subscriptionService.setUserSubscriptionInfo();
            return;
         }

         // Poll until claim is confirmed
         let shouldPoll = true;
         while (shouldPoll) {
            response = await this.apiService.claimPremiumAccess();
            if (response.status_code === 'ALREADY_CLAIMED') {
               shouldPoll = false;
               this.authService.setOnboardingTrialClaimed(true);
               await this.subscriptionService.setUserSubscriptionInfo();
            } else {
               await new Promise(resolve => setTimeout(resolve, 3000));
            }
         }
      } catch (error) {
         console.error('Error claiming premium access:', error);
         // Don't block the user flow on claim failure
      }
   }
   
}